import { useState, useMemo, useEffect, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { fightersSchedule2026 } from "@/mocks/fightersSchedule";

const carTypes = [
  "軽自動車",
  "普通車（コンパクト・セダン）",
  "ミニバン・大型",
  "キャンピングカー・特殊",
];

const arrivalTimes = [
  "9:00",
  "9:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "12:00",
  "12:30",
  "13:00",
  "13:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
  "17:00",
  "17:30",
  "18:00",
  "18:30",
  "19:00",
  "19:30",
];

export default function ReserveForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [searchParams] = useSearchParams();

  const preselectedDate = searchParams.get("game_date") || "";
  const [selectedDates, setSelectedDates] = useState<string[]>(() =>
    preselectedDate ? [preselectedDate] : []
  );
  const [dateError, setDateError] = useState(false);

  useEffect(() => {
    if (!preselectedDate) return;
    setDateError(false);
    setSelectedDates((prev) =>
      prev.includes(preselectedDate) ? prev : [...prev, preselectedDate]
    );
  }, [preselectedDate]);

  const toggleDate = (date: string) => {
    setDateError(false);
    setSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );
  };

  const upcomingGames = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return fightersSchedule2026
      .filter((g) => new Date(g.date) >= today)
      .map((g) => {
        const d = new Date(g.date);
        const month = d.getMonth() + 1;
        const day = d.getDate();
        return {
          ...g,
          displayLabel: `${month}/${day} (${g.dayOfWeek}) ${g.opponent} ${g.time}`,
        };
      });
  }, []);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg("");
    const form = e.currentTarget;
    const fd = new FormData(form);

    // honeypot check
    const honey = String(fd.get("company_alt") || "").trim();
    if (honey) {
      setStatus("success");
      setSelectedDates([]);
      form.reset();
      return;
    }

    if (selectedDates.length === 0) {
      setDateError(true);
      return;
    }
    setDateError(false);

    const message = String(fd.get("message") || "");
    if (message.length > 500) {
      setErrorMsg("メッセージは500文字以内でご入力ください。");
      return;
    }

    // 共通項目をまとめる（日付とハニーポットは除外）
    const params = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "company_alt" || key === "game_date") return;
      params.append(key, String(value));
    });
    // 選んだ試合日を日本語ラベルでまとめて送信して、1通の通知に全日を表示する
    const labelByDate = new Map(upcomingGames.map((g) => [g.date, g.displayLabel]));
    const dateLabels = selectedDates
      .map((d) => labelByDate.get(d) || d)
      .join("、");
    params.append("game_date", dateLabels);

    setStatus("loading");
    try {
      const res = await fetch("https://readdy.ai/api/form/d92bj580sqa2sn3dnerg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (!res.ok) throw new Error("network");
      setStatus("success");
      setSelectedDates([]);
      form.reset();
    } catch {
      setStatus("error");
      setErrorMsg("送信に失敗しました。時間をおいて再度お試しください。");
    }
  };

  return (
    <section id="reserve" className="relative py-24 md:py-32 px-6 md:px-10 overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Wide%20cinematic%20photo%20of%20a%20lush%20green%20baseball%20outfield%20blending%20into%20a%20soft%20terracotta%20sunset%20sky%20over%20Hokkaido%20countryside%2C%20warm%20natural%20light%2C%20editorial%20landscape%20photography%2C%20muted%20cream%20accents%2C%20high%20end%20cinematic%20composition%2C%20no%20text%20on%20image&width=1800&height=1000&seq=annette-cta-01&orientation=landscape"
        alt="reserve background"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/60" />

      <div className="relative max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5 text-background-50 lg:pt-6">
            <span className="font-heading italic text-secondary-200 text-sm tracking-[0.3em] uppercase">
              Reservation
            </span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl leading-tight">
              次の試合日、<br />
              駐車場の心配なしで<br />
              <span className="italic text-secondary-200">出発しよう。</span>
            </h2>
            <p className="mt-6 text-background-100 text-sm md:text-base leading-relaxed max-w-md">
              フォーム送信後、担当者よりご予約確定のご連絡をいたします。
              空き状況を最速で確認したい方は、お電話または公式LINEへ。
            </p>
            <div className="mt-8 flex items-center gap-3">
              <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-50 text-primary-600">
                <i className="ri-phone-fill text-lg" />
              </span>
              <div>
                <div className="text-xs text-background-200">
                  アネザキカンパニー内電話に繋がりますので駐車場利用のむねをお伝えください
                </div>
                <a
                  href="tel:0113753266"
                  className="font-heading text-lg md:text-xl text-background-50 tracking-wider cursor-pointer"
                >
                  ０１１－３７５－３２２６
                </a>
              </div>
            </div>
            <a
              href="http://lin.ee/8c9qzq8"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="mt-4 flex items-center gap-3 cursor-pointer group"
            >
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-800 group-hover:bg-secondary-200 transition-colors">
                <i className="ri-line-fill text-lg" />
              </span>
              <span className="text-sm text-background-100 group-hover:underline">
                LINE公式アカウント「アネットパーキング」でも予約受付中<br />
                お友達登録でお得なポイント配布中！
              </span>
            </a>
          </div>

          <div className="lg:col-span-7">
            <form
              id="reserve-form"
              data-readdy-form
              onSubmit={onSubmit}
              className="bg-background-50 rounded-3xl p-6 md:p-10 space-y-5"
            >
              {/* honeypot */}
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="field-supp"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-foreground-700 mb-1.5">
                    お名前
                  </label>
                  <input
                    required
                    name="name"
                    type="text"
                    placeholder="山田 太郎"
                    className="w-full px-4 py-3 rounded-md border border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-950 bg-background-50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-foreground-700 mb-1.5">
                    電話番号
                  </label>
                  <input
                    required
                    name="phone"
                    type="tel"
                    placeholder="090-1234-5678"
                    className="w-full px-4 py-3 rounded-md border border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-950 bg-background-50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-foreground-700 mb-1.5">
                  メールアドレス
                </label>
                <input
                  required
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-md border border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-950 bg-background-50"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs text-foreground-700 mb-1.5">
                    ご希望試合日（複数選択可）
                  </label>
                  <div className="rounded-md border border-background-300 bg-background-50 max-h-56 overflow-y-auto">
                    {upcomingGames.map((g) => {
                      const checked = selectedDates.includes(g.date);
                      return (
                        <label
                          key={g.date}
                          className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-background-200 last:border-b-0 transition-colors ${
                            checked ? "bg-primary-50" : "hover:bg-background-100"
                          }`}
                        >
                          <input
                            type="checkbox"
                            name="game_date"
                            value={g.date}
                            checked={checked}
                            onChange={() => toggleDate(g.date)}
                            className="w-4 h-4 accent-primary-500 cursor-pointer shrink-0"
                          />
                          <span className="text-sm text-foreground-950">
                            {g.displayLabel}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {dateError && (
                    <p className="mt-1.5 text-xs text-primary-700">
                      ご希望試合日を1つ以上お選びください。
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-foreground-700 mb-1.5">
                    車種
                  </label>
                  <select
                    required
                    name="car_type"
                    defaultValue=""
                    className="w-full px-4 py-3 pr-9 rounded-md border border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-950 bg-background-50 appearance-none"
                  >
                    <option value="" disabled>
                      車種をお選びください
                    </option>
                    {carTypes.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-foreground-700 mb-1.5">
                    人数
                  </label>
                  <input
                    required
                    name="people_count"
                    type="number"
                    min={1}
                    max={20}
                    placeholder="例：4名"
                    className="w-full px-4 py-3 rounded-md border border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-950 bg-background-50"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs text-foreground-700 mb-1.5">
                    希望到着時間
                  </label>
                  <select
                    required
                    name="arrival_time"
                    defaultValue=""
                    className="w-full px-4 py-3 pr-9 rounded-md border border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-950 bg-background-50 appearance-none"
                  >
                    <option value="" disabled>
                      到着時間をお選びください
                    </option>
                    {arrivalTimes.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs text-foreground-700 mb-1.5">
                  ご要望・メッセージ・【希望到着時間以外のご要望もお答えいたします】（任意・500文字以内）
                </label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="ベビーカー同伴、車椅子ご利用など"
                  className="w-full px-4 py-3 rounded-md border border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-950 bg-background-50 resize-none"
                />
              </div>

              <label className="flex items-start gap-2 text-xs text-foreground-700 cursor-pointer">
                <input
                  required
                  type="checkbox"
                  name="agree"
                  value="yes"
                  className="mt-0.5 accent-primary-500 cursor-pointer"
                />
                個人情報の取り扱いに同意し、予約リクエストを送信します。
              </label>

              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex items-center justify-center gap-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 rounded-full px-6 py-3 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      送信中...
                    </>
                  ) : (
                    <>
                      予約リクエストを送信
                      <i className="ri-arrow-right-line" />
                    </>
                  )}
                </button>
                {status === "success" && (
                  <span className="text-accent-700 text-sm inline-flex items-center gap-1.5">
                    <i className="ri-check-line" />
                    受付ました。担当者よりご連絡いたします。
                  </span>
                )}
                {status === "error" && errorMsg && (
                  <span className="text-primary-700 text-sm inline-flex items-center gap-1.5">
                    <i className="ri-error-warning-line" />
                    {errorMsg}
                  </span>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}